/*!
 * Vante — The cart that closes.
 * A conversion-engineered cart drawer for Shopify. No dependencies.
 * Works on any theme exposing the standard Cart AJAX API
 * (/cart.js, /cart/add.js, /cart/change.js, /cart/update.js).
 *
 * © WebVitals. Vante™. https://webvitals.org
 *
 * Drop-in usage (in theme.liquid, before </body>):
 *   <script>
 *     window.VanteConfig = { ...overrides... };
 *   </script>
 *   <script src="{{ 'vante.js' | asset_url }}" defer></script>
 *
 * Public API: window.Vante  (window.CartBooster kept as a back-compat alias)
 * Per-client customization is done entirely through window.VanteConfig.
 */
(function () {
  "use strict";

  if (window.__CartBoosterLoaded) return;
  window.__CartBoosterLoaded = true;

  /* ----------------------------------------------------------------------
   * 1. CONFIG  (every client store overrides these via CartBoosterConfig)
   * -------------------------------------------------------------------- */
  var DEFAULTS = {
    // --- Brand / look ---
    accent: "#1B3A2F",          // primary brand color (buttons, bar fill)
    accentText: "#FFFFFF",      // text on accent
    // Optional soft brand tint — a pale color used ONLY as gentle section
    // backgrounds (free-shipping bar zone, highlight blocks), never for
    // buttons or text. Leave "" to derive a tint from `accent` instead.
    accentSoft: "",
    radius: "14px",             // corner rounding
    fontFamily: "inherit",      // inherit the store's font by default
    position: "right",          // "right" | "left"
    width: "420px",

    // --- Free shipping bar ---
    freeShipping: {
      enabled: true,
      threshold: 7500,          // in cents (i.e. $75.00). MATCH the store's real rule.
      messageBefore: "You're {{amount}} away from FREE shipping",
      messageReached: "Free shipping unlocked.",
    },

    // --- Upsells / cross-sells ---
    upsells: {
      enabled: true,
      title: "Complete your routine",
      // Generic fallback shown when a suggested product has no specific
      // reason below. The free-shipping unlock reason always takes priority,
      // then a bundle reason, then a per-product reason, then this.
      reason: "A favorite from the routine",
      // Per-product reason lines, keyed by product HANDLE (the part after
      // /products/ in the URL). Replace each REPLACE-... with the real
      // handle from the store. A product not listed here just uses the
      // generic `reason` above — nothing breaks if a handle is wrong/missing.
      productReasons: {
        // Hot Oil Treatment — CONSEQUENCE. CSV literally explains that
        // shampoo "strips the hair bare before anything has a chance to
        // protect it." This is the truest, most urgent angle.
        "hot-oil-treatment": "Without it, the shampoo strips her hair bare.",
        // Miracle Hair Mask — RESULT. Her own words from the CSV.
        "miracle-hair-mask": "Walks away with hair that feels brand new.",
        // Moisturizing Shampoo — CONSEQUENCE inverted into reassurance.
        // The CSV's whole angle: "Most shampoos strip — not this one."
        "moisturizing-shampoo": "Most shampoos strip her hair. This one doesn't.",
        // Moisturizing Conditioner — RESULT, tactile. Direct CSV quote.
        "moisturizing-conditioner": "So soft she can't stop touching it.",
        // Moisturizer Milk — RESULT, time-anchored. Their own product tagline.
        "moisturizer-milk": "Soft in the morning. Still soft by bedtime.",
        // Avocado Leave-In — AUTHORITY. Tells you the role it plays.
        "avocado-leave-in-conditioner": "The finishing step that locks moisture in all day.",
        // Miracle Oil Coconut Free — CONSEQUENCE/differentiator. Owns the
        // problem coconut-oil products cause for some hair types.
        "miracle-oil-coconut-free": "For hair coconut oil leaves dry — finally.",
        // Hair Growth Serum — AUTHORITY. CSV's own headline angle.
        "hair-growth-serum": "Nature's most powerful growth boosters, in one bottle.",
        // Kit / set products — AUTHORITY + VALUE
        "wash-day": "The protective step every wash day needs.",
        "wash-day-1": "The protective step every wash day needs.",
        "full-natural-hair-care-set-1": "Her whole routine — every product, one price.",
        // Catalog items not in CSV yet — wired and ready for when added
        "hydrating-hair-butter": "Seals in moisture where her ends need it most.",
        "miracle-oil": "A few drops, all-day shine.",
        "edge-treatment": "Smooths edges that won't lay down.",
      },
      // "recommendations" uses Shopify's /recommendations/products.json (auto).
      // "manual" uses the variantIds list below.
      strategy: "recommendations",
      maxItems: 3,
      variantIds: [],           // e.g. [44012345678901, 44012345678902]
    },

    // --- Bundles (config map) ---
    // If a customer adds an individual product that belongs to a kit, Vante
    // offers the OTHER items in that same kit, with an honest reason, ABOVE
    // the generic recommendations. Bundle relationships are real ones you
    // define here — never guessed.
    //
    // Match is by product HANDLE: the last part of a product's URL.
    // Example: orahbelle.com/products/moisturizing-shampoo  ->  handle is
    // "moisturizing-shampoo". Open each product on the store, copy the bit
    // of the URL after /products/, and replace the placeholders below.
    //
    // Leave a bundle out (or its handles blank) and it simply won't fire —
    // nothing breaks. Only handles that actually exist as products will be
    // offered, so listing an item that isn't a separate product is harmless.
    bundles: {
      enabled: true,
      reasonTemplate: "Completes the {{bundle}}",
      // Products that ARE a complete kit/bundle on their own. When any of
      // these is in the cart, the customer has bought a finished set and is
      // in "let me pay" mode — Vante suppresses ALL upsells and keeps the
      // cart clean so nothing competes with checkout. Use product handles
      // (the part after /products/ in the URL), same as below.
      kitProductHandles: [
        "wash-day",
        "wash-day-1",
        "full-natural-hair-care-set-1",
      ],
      groups: [
        {
          name: "Hair Wash Kit",
          handles: [
            "hot-oil-treatment",
            "miracle-hair-mask",
            "moisturizing-shampoo",
            "moisturizing-conditioner",
          ],
        },
        {
          name: "Hair Hydration Kit",
          handles: [
            "moisturizer-milk",
            "avocado-leave-in-conditioner",
            "hydrating-hair-butter",
          ],
        },
        {
          name: "Scalp Nourishment Kit",
          handles: [
            "miracle-oil",
            "hair-growth-serum",
            "edge-treatment",
          ],
        },
      ],
    },

    // --- Close-the-sale mode ---
    // When the cart is already large, the shopper is in "let me pay" mode,
    // not "show me more" mode. Pushing upsells here adds friction and
    // doubt. Above the threshold (or if a complete kit is in the cart),
    // Vante hides all upsells and shows a confident closing message plus a
    // subtle trust line, so nothing competes with checkout.
    closeTheSale: {
      enabled: true,
      threshold: 8000,          // cents. Over $80 -> close-the-sale mode.
      message: "You've got everything you need.",
      trustLine: "Secure checkout · Easy returns",
    },

    // --- Discount field ---
    discount: {
      enabled: true,
      placeholder: "Discount code",
    },

    // --- Urgency cues ---
    urgency: {
      lowStock: true,           // shows "Only N left" when inventory is low & tracked
      lowStockThreshold: 8,
      // Fake countdowns can BACKFIRE on savvy shoppers (reads as manipulation
      // and erodes trust -> fewer sales). Off by default on purpose. Real,
      // inventory-based "Only N left" above stays on because it's truthful.
      reservationTimer: false,  // soft "items reserved for MM:SS" countdown
      reservationMinutes: 10,
      reservationText: "We're holding your cart for {{time}}",
    },

    // --- Abandoned-cart (client-side layer) ---
    recovery: {
      persist: true,            // remember cart contents across visits
      exitIntent: true,         // desktop exit-intent nudge
      exitIntentText: "Wait — your cart is still here. Here's 10% to finish up.",
      // A REAL Shopify discount code. Must exist in the store's Discounts
      // admin or it will fail at checkout. Leave "" to show no code.
      exitIntentCode: "FINISH10",
      // When true, clicking the nudge's button auto-applies exitIntentCode
      // to the cart and sends the shopper to checkout with it already on.
      exitIntentApply: true,
      returningNudge: true,     // gentle reminder badge for returning visitors
      // Mobile (and desktop) recovery: if the shopper opens the cart, closes
      // it WITHOUT checking out, and then stays completely still (no scroll,
      // no tap) for `closeNudgeDelay` ms, show the same FINISH10 offer.
      // Fires at most once per visit. This is the mobile-safe counterpart to
      // the desktop exit-intent nudge — it needs no mouse.
      closeNudge: true,
      closeNudgeDelay: 3000,
      // Hook: fires when a cart is "at risk". Wire this into Klaviyo / PushOwl.
      onAtRisk: null,           // function(cart) {}
    },

    // --- Branding ---
    branding: {
      showBadge: true,          // tasteful "Vante" mark at the bottom of the drawer
      label: "Vante",
      href: "https://webvitals.org",
      // White-label: set showBadge:false for premium clients who pay to remove it.
    },

    // --- Auto-branding ---
    // Zero-config: Vante reads the store's real Add-to-cart button + body
    // font on load and themes itself to match. No per-client color setup.
    // Any value explicitly set in VanteConfig still wins (safety net for
    // themes where detection can't get a confident read).
    autoBrand: true,

    // --- Behavior ---
    openOnAdd: true,            // open drawer automatically after add-to-cart
    interceptAddToCart: true,   // hijack theme add-to-cart forms for AJAX add
    checkoutUrl: "/checkout",
    texts: {
      heading: "Your Cart",
      // Small editorial line above the cart heading. Empty = nothing shown.
      // Per-client: the store's tagline reads beautifully here, e.g.
      // "Nourish · Restore · Glow" for Orahbelle.
      kicker: "",
      empty: "Your cart is empty",
      emptyCta: "Continue shopping",
      checkout: "Checkout",
      subtotal: "Subtotal",
      remove: "Remove",
      taxesNote: "Shipping & taxes calculated at checkout",
    },
  };

  function deepMerge(base, over) {
    var out = {};
    for (var k in base) out[k] = base[k];
    if (!over) return out;
    for (var key in over) {
      if (
        over[key] && typeof over[key] === "object" &&
        !Array.isArray(over[key]) && typeof base[key] === "object"
      ) {
        out[key] = deepMerge(base[key], over[key]);
      } else {
        out[key] = over[key];
      }
    }
    return out;
  }

  var USERCFG = window.VanteConfig || window.CartBoosterConfig || {};
  var CFG = deepMerge(DEFAULTS, USERCFG);

  /* ----------------------------------------------------------------------
   * 2. UTILITIES
   * -------------------------------------------------------------------- */
  var STORE_CURRENCY =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";

  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: STORE_CURRENCY,
      }).format((cents || 0) / 100);
    } catch (e) {
      return "$" + ((cents || 0) / 100).toFixed(2);
    }
  }

  /* ----------------------------------------------------------------------
   * 2c. ANALYTICS  (Shopify Customer Events + GA4 + Meta + TikTok)
   *
   * One central track() function emits each cart event to ALL connected
   * platforms simultaneously. Each call is wrapped in safety checks so a
   * missing pixel is a silent no-op, never an error. This lets the same
   * code work whether the store has 0, 1, 2, 3, or 4 of these pixels
   * installed — no flag-flipping per client.
   *
   * Standard payload shape:
   *   { value: cents, currency: "USD", items: [{ id, name, price, qty }],
   *     ...event-specific extras }
   *
   * Event names use cart_* prefix so they're clearly distinguishable from
   * Shopify's own checkout_* and product_* events in dashboards.
   * -------------------------------------------------------------------- */
  function trackVante(event, payload) {
    payload = payload || {};
    var safePayload = {
      event: event,
      timestamp: Date.now(),
      currency: STORE_CURRENCY,
      cart_value: (STATE.cart && STATE.cart.total_price) || 0,
      cart_count: (STATE.cart && STATE.cart.item_count) || 0,
    };
    // merge incoming payload
    for (var k in payload) safePayload[k] = payload[k];

    // 1) Shopify Customer Events (propagates to Meta, TikTok, GA4 via Shopify
    //    pipeline — the official, preferred path).
    try {
      if (window.Shopify && window.analytics &&
          typeof window.analytics.publish === "function") {
        window.analytics.publish("vante_" + event, safePayload);
      }
    } catch (e) {}

    // 2) Direct Google Analytics 4 (gtag) — belt-and-suspenders.
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "vante_" + event, {
          currency: safePayload.currency,
          value: (safePayload.value || 0) / 100, // GA4 wants dollars
          cart_value: safePayload.cart_value / 100,
          cart_count: safePayload.cart_count,
          item_id: safePayload.item_id || undefined,
          item_name: safePayload.item_name || undefined,
          item_price: safePayload.item_price ?
            safePayload.item_price / 100 : undefined,
        });
      }
    } catch (e) {}

    // 3) Direct Meta Pixel (fbq) — backup. Map cart events to recognized
    //    Meta standard events where they map cleanly; custom otherwise.
    try {
      if (typeof window.fbq === "function") {
        var metaMap = {
          upsell_added: "AddToCart",
          checkout_clicked: "InitiateCheckout",
          threshold_crossed: "AddToCart",
        };
        var metaEvent = metaMap[event];
        if (metaEvent) {
          window.fbq("track", metaEvent, {
            value: (safePayload.value || safePayload.cart_value || 0) / 100,
            currency: safePayload.currency,
            content_ids: safePayload.item_id ? [safePayload.item_id] : undefined,
            content_name: safePayload.item_name,
          });
        } else {
          window.fbq("trackCustom", "Vante_" + event, safePayload);
        }
      }
    } catch (e) {}

    // 4) Direct TikTok Pixel (ttq) — backup. Same mapping principle.
    try {
      if (window.ttq && typeof window.ttq.track === "function") {
        var ttMap = {
          upsell_added: "AddToCart",
          checkout_clicked: "InitiateCheckout",
        };
        var ttEvent = ttMap[event];
        if (ttEvent) {
          window.ttq.track(ttEvent, {
            value: (safePayload.value || 0) / 100,
            currency: safePayload.currency,
            content_id: safePayload.item_id,
            content_name: safePayload.item_name,
          });
        }
      }
    } catch (e) {}
  }

  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) for (var a in attrs) {
      if (a === "class") n.className = attrs[a];
      else if (a === "html") n.innerHTML = attrs[a];
      else n.setAttribute(a, attrs[a]);
    }
    if (html != null) n.innerHTML = html;
    return n;
  }

  function api(url, opts) {
    return fetch(url, Object.assign({
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
    }, opts || {})).then(function (r) {
      if (!r.ok) return r.json().then(function (e) { throw e; });
      return r.json();
    });
  }

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  /* ----------------------------------------------------------------------
   * 2b. ZERO-CONFIG BRAND DETECTION
   * Reads the store's real Add-to-cart button + body font on load and
   * themes Vante to match. Anything explicitly set in VanteConfig wins.
   * -------------------------------------------------------------------- */
  function parseColor(str) {
    if (!str) return null;
    str = String(str).trim();
    var m = str.match(/^rgba?\(([^)]+)\)/i);
    if (m) {
      var p = m[1].split(/[ ,/]+/).map(parseFloat);
      return { r: p[0], g: p[1], b: p[2], a: p[3] == null ? 1 : p[3] };
    }
    m = str.match(/^#([0-9a-f]{3,8})$/i);
    if (m) {
      var h = m[1];
      if (h.length === 3 || h.length === 4)
        h = h.split("").map(function (c) { return c + c; }).join("");
      return {
        r: parseInt(h.substr(0, 2), 16),
        g: parseInt(h.substr(2, 2), 16),
        b: parseInt(h.substr(4, 2), 16),
        a: h.length >= 8 ? parseInt(h.substr(6, 2), 16) / 255 : 1,
      };
    }
    // OS 2.0 themes often store accents as a bare "18, 58, 47" triplet
    var t = str.match(/^(\d+)[ ,]+(\d+)[ ,]+(\d+)$/);
    if (t) return { r: +t[1], g: +t[2], b: +t[3], a: 1 };
    return null;
  }

  function lum(c) {
    var a = [c.r, c.g, c.b].map(function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
  }

  function rgbStr(c) {
    return "rgb(" + Math.round(c.r) + "," + Math.round(c.g) + "," +
      Math.round(c.b) + ")";
  }

  function readableText(bg) {
    return lum(bg) > 0.42 ? "#14201C" : "#FFFFFF";
  }

  function usableBg(c) {
    // Reject transparent / ghost / near-white buttons — not a brand color.
    return !!c && c.a >= 0.55 && lum(c) <= 0.93;
  }

  var ATC_SELECTORS = [
    'form[action*="/cart/add"] button[type="submit"]',
    'form[action*="/cart/add"] [type="submit"]',
    'button[name="add"]', "[data-add-to-cart]",
    ".product-form__submit", ".product-form__cart-submit",
    ".add-to-cart", ".btn--add-to-cart", ".product__add-to-cart",
  ];

  function findATC() {
    for (var i = 0; i < ATC_SELECTORS.length; i++) {
      var n = document.querySelector(ATC_SELECTORS[i]);
      if (n) return n;
    }
    var btns = document.querySelectorAll(
      'button,input[type="submit"],a.button,a.btn');
    for (var j = 0; j < btns.length; j++) {
      var t = (btns[j].textContent || btns[j].value || "").toLowerCase();
      if (/add to (cart|bag|basket)/.test(t)) return btns[j];
    }
    return null;
  }

  var THEME_VARS = [
    "--color-button", "--colorBtnPrimary", "--color-base-accent-1",
    "--color-primary", "--color-accent", "--colors-accent",
    "--button-background", "--color-base-solid-button-labels",
  ];

  function detectBrand() {
    var out = {};
    try {
      var bf = getComputedStyle(document.body).fontFamily;
      if (bf) out.fontFamily = bf;
    } catch (e) {}

    var btn = findATC();
    if (btn) {
      try {
        var cs = getComputedStyle(btn);
        var bg = parseColor(cs.backgroundColor);
        if (usableBg(bg)) {
          out.accent = rgbStr(bg);
          var tc = parseColor(cs.color);
          out.accentText =
            tc && Math.abs(lum(tc) - lum(bg)) > 0.3
              ? rgbStr(tc) : readableText(bg);
        }
        var br = (cs.borderRadius || "").split(" ")[0];
        if (br && br !== "0px" && parseFloat(br) >= 0) out.radius = br;
      } catch (e) {}
    }

    if (!out.accent) {
      try {
        var root = getComputedStyle(document.documentElement);
        for (var i = 0; i < THEME_VARS.length; i++) {
          var raw = root.getPropertyValue(THEME_VARS[i]);
          var c = parseColor(raw && raw.trim());
          if (usableBg(c)) {
            out.accent = rgbStr(c);
            out.accentText = readableText(c);
            break;
          }
        }
      } catch (e) {}
    }
    return out;
  }

  function applyAutoBrand() {
    if (CFG.autoBrand === false) return;
    var d = detectBrand();
    ["accent", "accentText", "radius", "fontFamily"].forEach(function (k) {
      // Auto value only fills in where the merchant didn't set it explicitly.
      if (d[k] != null && !(k in USERCFG)) CFG[k] = d[k];
    });
  }

  /* ----------------------------------------------------------------------
   * 3. STYLES  (scoped under .cb- so it never fights the theme)
   * -------------------------------------------------------------------- */
  function injectStyles() {
    if (document.getElementById("cb-styles")) return;

    // Load the editorial display serif used for cart headings & subtotal.
    // Beauty-brand-warm, not generic. Falls back gracefully if blocked.
    if (!document.getElementById("cb-font")) {
      var fLink = document.createElement("link");
      fLink.id = "cb-font";
      fLink.rel = "stylesheet";
      fLink.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,500;0,600;1,500&display=swap";
      document.head.appendChild(fLink);
    }

    var css = `
    :root{
      --cb-accent:${CFG.accent};
      --cb-accent-text:${CFG.accentText};
      --cb-accent-soft:${CFG.accentSoft || "color-mix(in srgb, " + CFG.accent + " 10%, #fff)"};
      --cb-ink:#15201C;
      --cb-mute:#7A857F;
      --cb-line:#ECEFEC;
      --cb-paper:#FCFBF8;
      --cb-radius:${CFG.radius};
      --cb-w:${CFG.width};
      --cb-display:"Cormorant Garamond","Playfair Display",Georgia,serif;
      --cb-body:${CFG.fontFamily};
    }
    .cb-root,.cb-root *{box-sizing:border-box;}
    .cb-root{font-family:var(--cb-body);color:var(--cb-ink);
      -webkit-font-smoothing:antialiased;}

    /* OVERLAY ---------------------------------------------------------- */
    .cb-overlay{position:fixed;inset:0;background:rgba(15,20,18,.42);
      backdrop-filter:saturate(125%) blur(3px);
      -webkit-backdrop-filter:saturate(125%) blur(3px);
      opacity:0;pointer-events:none;
      transition:opacity .32s cubic-bezier(.22,1,.36,1);z-index:2147483000;}
    .cb-overlay.cb-on{opacity:1;pointer-events:auto;}

    /* DRAWER ----------------------------------------------------------- */
    .cb-drawer{position:fixed;top:0;${CFG.position}:0;height:100vh;height:100dvh;
      width:var(--cb-w);max-width:92vw;background:var(--cb-paper);
      display:flex;flex-direction:column;
      box-shadow:0 0 80px -10px rgba(20,40,32,.22);z-index:2147483001;
      transform:translateX(${CFG.position === "left" ? "-104%" : "104%"});
      transition:transform .42s cubic-bezier(.22,1,.36,1);
      background-image:
        radial-gradient(circle at 100% 0%,var(--cb-accent-soft) 0%,transparent 36%),
        radial-gradient(circle at 0% 100%,var(--cb-accent-soft) 0%,transparent 32%);
      background-attachment:local;}
    .cb-drawer.cb-on{transform:translateX(0);}

    /* HEADER ---------------------------------------------------------- */
    .cb-head{display:flex;align-items:flex-start;justify-content:space-between;
      padding:24px 26px 18px;
      padding-top:max(24px,env(safe-area-inset-top));
      border-bottom:1px solid var(--cb-line);position:relative;}
    .cb-head::after{content:"";position:absolute;left:26px;right:26px;bottom:-1px;
      height:1px;background:linear-gradient(90deg,transparent,
        color-mix(in srgb,var(--cb-accent) 50%,transparent),transparent);}
    .cb-title-wrap{display:flex;flex-direction:column;gap:2px;min-width:0;}
    .cb-kicker{font-size:10px;font-weight:700;letter-spacing:.22em;
      text-transform:uppercase;color:var(--cb-accent);}
    .cb-head h2{margin:0;font-family:var(--cb-display);font-size:28px;
      font-weight:500;letter-spacing:-.01em;color:var(--cb-ink);line-height:1;}
    .cb-x{appearance:none;border:0;background:transparent;width:44px;height:44px;
      border-radius:50%;cursor:pointer;color:var(--cb-ink);
      transition:background .18s,transform .18s;
      display:flex;align-items:center;justify-content:center;flex:none;}
    .cb-x:hover{background:color-mix(in srgb,var(--cb-ink) 6%,transparent);
      transform:rotate(90deg);}

    /* FREE-SHIPPING ZONE ---------------------------------------------- */
    .cb-ship{padding:20px 26px 8px;}
    .cb-ship-msg{font-size:13px;font-weight:500;color:var(--cb-ink);
      margin:0 0 11px;letter-spacing:.005em;}
    .cb-ship-msg b{color:var(--cb-ink);font-weight:800;
      font-variant-numeric:tabular-nums;}
    .cb-track{height:7px;background:color-mix(in srgb,var(--cb-ink) 8%,transparent);
      border-radius:99px;overflow:hidden;position:relative;}
    .cb-fill{height:100%;width:0;border-radius:99px;
      background:linear-gradient(90deg,
        color-mix(in srgb,var(--cb-accent) 88%,#000),
        var(--cb-accent));
      transition:width .65s cubic-bezier(.22,1,.36,1);position:relative;}
    .cb-fill::after{content:"";position:absolute;inset:0;
      background:linear-gradient(90deg,transparent 30%,
        rgba(255,255,255,.55) 50%,transparent 70%);
      background-size:200% 100%;background-position:200% 0;
      animation:cb-shimmer 2.4s ease-in-out infinite;}
    @keyframes cb-shimmer{
      0%,100%{background-position:200% 0;}
      50%{background-position:-200% 0;}
    }
    .cb-ship.cb-reached .cb-ship-msg{color:#1B241F;font-weight:800;
      animation:cb-pop .5s ease;}
    @keyframes cb-pop{
      0%{transform:scale(.94);}
      60%{transform:scale(1.04);}
      100%{transform:scale(1);}
    }
    .cb-resv{margin:12px 26px 0;font-size:12px;color:var(--cb-mute);
      display:flex;align-items:center;gap:6px;}
    .cb-resv b{color:#C2410C;font-variant-numeric:tabular-nums;}

    /* CART LINES ------------------------------------------------------ */
    .cb-body{flex:1;overflow-y:auto;padding:10px 0 4px;
      overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}
    .cb-line{display:flex;gap:14px;padding:18px 26px;
      border-bottom:1px solid var(--cb-line);
      animation:cb-in .42s cubic-bezier(.22,1,.36,1) both;position:relative;}
    @keyframes cb-in{
      from{opacity:0;transform:translateY(10px);}
      to{opacity:1;transform:none;}
    }
    /* JUST-ADDED line pulse — bold brand wash sweeps across the line once */
    .cb-line.cb-just-added{animation:cb-in .42s both, cb-just-added 1s ease;}
    @keyframes cb-just-added{
      0%   {background:var(--cb-accent-soft);transform:scale(1.012);}
      40%  {background:var(--cb-accent-soft);transform:scale(1);}
      100% {background:transparent;transform:scale(1);}
    }
    /* JUST-UNLOCKED celebration — message animates UP and ENDS dark + bold.
       Critical: must end at opacity:1 and dark color so the text stays
       clearly readable AFTER the celebration finishes. */
    .cb-ship.cb-just-unlocked .cb-ship-msg{
      animation:cb-unlocked-msg .9s cubic-bezier(.22,1,.36,1);}
    @keyframes cb-unlocked-msg{
      0%   {transform:scale(.92);opacity:.55;color:var(--cb-accent);}
      35%  {transform:scale(1.10);opacity:1;color:var(--cb-accent);
            letter-spacing:.04em;}
      100% {transform:scale(1);opacity:1;color:#1B241F;letter-spacing:0;}
    }
    .cb-ship.cb-just-unlocked .cb-track{
      animation:cb-unlocked-track .9s cubic-bezier(.22,1,.36,1);}
    @keyframes cb-unlocked-track{
      0%,100%{box-shadow:0 0 0 0 transparent;}
      40%    {box-shadow:0 0 0 6px color-mix(in srgb,var(--cb-accent) 30%,transparent);}
    }
    /* READY-TO-CHECKOUT — subtle confident pulse, ONLY when free shipping earned */
    .cb-co.cb-co-ready{animation:cb-ready-pulse 2.4s ease-in-out infinite;}
    @keyframes cb-ready-pulse{
      0%,100%{box-shadow:0 0 0 0 color-mix(in srgb,var(--cb-accent) 0%,transparent);}
      50%    {box-shadow:0 0 0 6px color-mix(in srgb,var(--cb-accent) 24%,transparent);}
    }
    .cb-thumb{width:72px;height:72px;border-radius:12px;object-fit:cover;
      background:var(--cb-accent-soft);flex:none;
      box-shadow:0 1px 2px rgba(20,40,32,.04);}
    .cb-info{flex:1;min-width:0;display:flex;flex-direction:column;gap:6px;}
    .cb-title{font-size:14.5px;font-weight:600;color:var(--cb-ink);
      margin:0;line-height:1.3;letter-spacing:-.005em;}
    .cb-variant{font-size:12px;color:var(--cb-mute);margin:0;letter-spacing:.01em;}
    .cb-low{display:inline-block;width:fit-content;font-size:10px;font-weight:700;
      color:#C2410C;background:#FFF1E7;padding:3px 9px;border-radius:99px;
      margin:0;letter-spacing:.06em;text-transform:uppercase;}
    .cb-qty{display:inline-flex;align-items:center;
      border:1px solid var(--cb-line);
      border-radius:99px;overflow:hidden;background:#fff;width:fit-content;}
    .cb-qty button{appearance:none;border:0;background:#fff;width:38px;height:38px;
      cursor:pointer;font-size:16px;color:var(--cb-ink);transition:background .12s;
      touch-action:manipulation;}
    .cb-qty button:hover{background:var(--cb-accent-soft);}
    .cb-qty span{min-width:34px;text-align:center;font-size:13px;font-weight:700;
      font-variant-numeric:tabular-nums;}
    .cb-right{display:flex;flex-direction:column;align-items:flex-end;
      justify-content:space-between;gap:6px;}
    .cb-price{font-size:15px;font-weight:700;color:var(--cb-ink);
      font-variant-numeric:tabular-nums;letter-spacing:-.01em;}
    .cb-rm{appearance:none;border:0;background:none;color:var(--cb-mute);
      font-size:11px;cursor:pointer;text-decoration:underline;
      text-underline-offset:2px;padding:8px 6px;margin:-6px -6px;
      touch-action:manipulation;transition:color .15s;}
    .cb-rm:hover{color:#C2410C;}

    /* UPSELLS --------------------------------------------------------- */
    .cb-up{padding:22px 26px 10px;border-top:1px solid var(--cb-line);
      margin-top:8px;position:relative;}
    .cb-up::before{content:"";position:absolute;top:0;left:50%;width:60px;
      height:1px;background:var(--cb-accent);transform:translateX(-50%);}
    .cb-up h3{margin:0 0 14px;font-family:var(--cb-display);
      font-size:18px;font-weight:500;color:var(--cb-ink);
      letter-spacing:-.005em;text-align:center;font-style:italic;}
    .cb-up-row{display:flex;gap:13px;align-items:center;padding:11px 0;
      animation:cb-in .42s ease both;}
    .cb-up-row img{width:54px;height:54px;border-radius:10px;object-fit:cover;
      background:var(--cb-accent-soft);flex:none;}
    .cb-ph{width:54px;height:54px;border-radius:10px;flex:none;display:flex;
      align-items:center;justify-content:center;font-family:var(--cb-display);
      font-size:22px;font-weight:500;color:var(--cb-accent-text);
      background:linear-gradient(135deg,var(--cb-accent),
        color-mix(in srgb,var(--cb-accent) 65%,#fff));}
    .cb-up-row.cb-unlock{background:var(--cb-accent-soft);margin:6px -26px;
      padding:14px 26px;border-radius:0;position:relative;}
    .cb-up-row.cb-unlock::before{content:"";position:absolute;left:0;top:0;
      bottom:0;width:3px;background:var(--cb-accent);}
    .cb-unlock-tag{display:block;font-size:10px;font-weight:800;
      color:var(--cb-accent);letter-spacing:.12em;text-transform:uppercase;
      margin:0 0 5px;}
    .cb-up-meta{flex:1;min-width:0;}
    .cb-up-meta p{margin:0;font-size:14px;font-weight:600;color:var(--cb-ink);
      white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .cb-up-meta span{font-size:12px;color:var(--cb-mute);}
    .cb-up-meta .cb-up-price{display:inline-block;font-weight:700;
      color:var(--cb-ink);font-variant-numeric:tabular-nums;}
    .cb-up-meta .cb-up-reason{display:block;font-size:12px;color:#1B241F;
      margin-top:4px;white-space:normal;line-height:1.45;letter-spacing:.005em;
      font-weight:500;}
    .cb-up-meta .cb-up-bundle{color:#1B241F;font-weight:800;
      letter-spacing:.005em;}
    .cb-add{appearance:none;border:0;
      background:var(--cb-accent);color:var(--cb-accent-text);
      font-size:13px;font-weight:700;padding:10px 20px;
      border-radius:99px;cursor:pointer;flex:none;
      transition:filter .15s,transform .12s;touch-action:manipulation;
      letter-spacing:.02em;
      box-shadow:0 2px 0 color-mix(in srgb,var(--cb-accent) 70%,#000);}
    .cb-add:hover{filter:brightness(1.06);}
    .cb-add:active{transform:translateY(1px);
      box-shadow:0 1px 0 color-mix(in srgb,var(--cb-accent) 70%,#000);}

    /* CLOSE-THE-SALE MODE -------------------------------------------- */
    .cb-close{padding:18px 0 8px;text-align:center;}
    .cb-close-check{width:54px;height:54px;border-radius:50%;margin:0 auto 14px;
      display:flex;align-items:center;justify-content:center;
      color:#fff;background:var(--cb-accent);
      box-shadow:0 4px 14px -4px color-mix(in srgb,var(--cb-accent) 60%,#000);
      animation:cb-pop .5s cubic-bezier(.22,1,.36,1);}
    .cb-close-msg{margin:0 0 5px;font-family:var(--cb-display);font-size:20px;
      font-weight:600;color:#1B241F;letter-spacing:-.005em;}
    .cb-close-trust{margin:0;font-size:12px;color:#4A524D;font-weight:500;
      letter-spacing:.03em;}

    /* DISCOUNT FIELD ------------------------------------------------- */
    .cb-disc{display:flex;gap:8px;padding:16px 26px 4px;}
    .cb-disc input{flex:1;border:1px solid var(--cb-line);
      border-radius:12px;padding:13px 14px;font-size:16px;outline:none;
      background:#fff;font-family:var(--cb-body);color:var(--cb-ink);
      transition:border-color .15s;}
    .cb-disc input:focus{border-color:var(--cb-accent);}
    .cb-disc button{border:0;background:var(--cb-ink);color:#fff;font-size:13px;
      font-weight:700;padding:0 22px;border-radius:12px;cursor:pointer;
      min-height:48px;touch-action:manipulation;letter-spacing:.04em;}
    .cb-disc-ok{padding:0 26px 8px;font-size:12px;font-weight:600;color:#15803D;}
    .cb-disc-err{padding:0 26px 8px;font-size:12px;font-weight:600;color:#C2410C;}
    .cb-chip{display:flex;align-items:center;gap:8px;margin:0 26px 10px;
      background:color-mix(in srgb,#15803D 10%,#fff);
      border:1px solid color-mix(in srgb,#15803D 25%,#fff);
      border-radius:12px;padding:11px 13px;font-size:13px;font-weight:600;
      color:#15803D;}
    .cb-chip button{margin-left:auto;border:0;background:none;color:#15803D;
      cursor:pointer;font-size:16px;line-height:1;opacity:.7;}
    .cb-chip button:hover{opacity:1;}

    /* FOOTER + CHECKOUT --------------------------------------------- */
    .cb-foot{border-top:1px solid var(--cb-line);background:#fff;
      padding:20px 26px max(24px,env(safe-area-inset-bottom));}
    .cb-sub{display:flex;justify-content:space-between;align-items:baseline;
      margin:0 0 5px;}
    .cb-sub span{font-size:12px;color:var(--cb-mute);font-weight:600;
      letter-spacing:.08em;text-transform:uppercase;}
    .cb-sub b{font-family:var(--cb-display);font-size:26px;color:var(--cb-ink);
      font-variant-numeric:tabular-nums;font-weight:500;letter-spacing:-.01em;}
    .cb-note{font-size:11px;color:var(--cb-mute);margin:0 0 16px;
      letter-spacing:.02em;}
    .cb-co{display:flex;align-items:center;justify-content:center;gap:10px;
      width:100%;background:var(--cb-ink);color:#fff;border:0;
      border-radius:var(--cb-radius);padding:18px;font-size:15px;
      font-weight:700;cursor:pointer;text-decoration:none;
      transition:filter .15s,transform .1s;touch-action:manipulation;
      letter-spacing:.04em;position:relative;overflow:hidden;}
    .cb-co::before{content:"";position:absolute;inset:0;
      background:linear-gradient(90deg,transparent,rgba(255,255,255,.18),transparent);
      transform:translateX(-100%);transition:transform .8s;}
    .cb-co:hover::before{transform:translateX(100%);}
    .cb-co:active{transform:scale(.99);}
    .cb-co svg{transition:transform .2s;}
    .cb-co:hover svg{transform:translateX(3px);}
    .cb-badge{display:flex;align-items:center;justify-content:center;gap:6px;
      margin-top:14px;font-size:10.5px;letter-spacing:.06em;color:#B8BFB9;
      font-family:var(--cb-body);text-decoration:none;}
    .cb-badge:hover{color:#7A857F;}
    .cb-badge b{font-weight:800;letter-spacing:.08em;}

    /* EMPTY STATE --------------------------------------------------- */
    .cb-empty{text-align:center;padding:72px 28px;color:var(--cb-mute);}
    .cb-empty .cb-empty-icon{width:64px;height:64px;margin:0 auto 18px;
      border-radius:50%;background:var(--cb-accent-soft);
      display:flex;align-items:center;justify-content:center;
      color:var(--cb-accent);}
    .cb-empty p{font-family:var(--cb-display);font-size:18px;font-style:italic;
      margin:0 0 22px;color:var(--cb-ink);font-weight:500;}
    .cb-empty button{border:1px solid var(--cb-ink);background:transparent;
      border-radius:99px;padding:13px 26px;font-size:13px;font-weight:700;
      cursor:pointer;color:var(--cb-ink);letter-spacing:.04em;
      transition:background .15s,color .15s;touch-action:manipulation;}
    .cb-empty button:hover{background:var(--cb-ink);color:#fff;}

    /* TOAST / NUDGE ------------------------------------------------- */
    .cb-toast{position:fixed;background:var(--cb-ink);
      bottom:max(22px,calc(env(safe-area-inset-bottom) + 14px));
      ${CFG.position}:max(22px,env(safe-area-inset-${CFG.position}));
      color:#fff;padding:16px 20px;border-radius:14px;
      font-size:13.5px;font-weight:500;line-height:1.5;
      box-shadow:0 16px 36px -8px rgba(0,0,0,.32);z-index:2147483002;
      transform:translateY(180%);
      transition:transform .42s cubic-bezier(.22,1,.36,1);max-width:340px;}
    .cb-toast.cb-on{transform:translateY(0);}
    .cb-toast b{color:var(--cb-accent);font-weight:700;}
    .cb-toast .cb-toast-x{position:absolute;top:8px;right:10px;background:none;
      border:0;color:#9AA7A0;font-size:18px;cursor:pointer;line-height:1;
      width:32px;height:32px;touch-action:manipulation;}
    .cb-toast .cb-toast-cta{display:inline-block;margin-top:12px;background:#fff;
      color:var(--cb-ink);border:0;border-radius:10px;padding:10px 18px;
      font-size:13px;font-weight:700;cursor:pointer;text-decoration:none;
      letter-spacing:.03em;touch-action:manipulation;}

    /* BUBBLE ------------------------------------------------------- */
    .cb-bubble{position:fixed;z-index:2147482999;
      bottom:max(24px,calc(env(safe-area-inset-bottom) + 16px));
      ${CFG.position}:max(20px,env(safe-area-inset-${CFG.position}));
      background:var(--cb-accent);color:var(--cb-accent-text);border:0;
      border-radius:99px;height:58px;min-width:58px;padding:0 22px;cursor:pointer;
      font-size:14px;font-weight:700;display:flex;align-items:center;gap:9px;
      box-shadow:0 12px 32px -8px color-mix(in srgb,var(--cb-accent) 60%,#000);
      transition:transform .18s,box-shadow .18s;touch-action:manipulation;
      letter-spacing:.03em;}
    .cb-bubble:hover{transform:translateY(-2px);
      box-shadow:0 16px 38px -8px color-mix(in srgb,var(--cb-accent) 70%,#000);}
    .cb-bubble .cb-count{background:rgba(255,255,255,.28);border-radius:99px;
      min-width:24px;height:24px;display:flex;align-items:center;justify-content:center;
      font-size:12px;padding:0 7px;font-variant-numeric:tabular-nums;}

    /* LOADER ------------------------------------------------------- */
    .cb-spin{width:32px;height:32px;
      border:3px solid color-mix(in srgb,var(--cb-ink) 10%,transparent);
      border-top-color:var(--cb-accent);border-radius:50%;margin:48px auto;
      animation:cb-spin .8s linear infinite;}
    @keyframes cb-spin{to{transform:rotate(360deg);}}

    /* MOBILE ------------------------------------------------------- */
    @media (max-width:520px){
      .cb-drawer{width:100vw;max-width:100vw;}
      .cb-toast{left:14px;right:14px;max-width:none;
        ${CFG.position}:14px;}
      .cb-toast .cb-toast-cta{display:block;text-align:center;margin-top:14px;}
      .cb-head,.cb-ship,.cb-line,.cb-disc,.cb-foot,.cb-up{padding-left:20px;padding-right:20px;}
      .cb-up-row.cb-unlock{margin:6px -20px;padding-left:20px;padding-right:20px;}
      .cb-head h2{font-size:24px;}
      .cb-sub b{font-size:22px;}
    }
    @media (hover:none){
      .cb-x:hover{transform:none;background:transparent;}
      .cb-co:hover{filter:none;}
      .cb-co:hover::before{transform:translateX(-100%);}
      .cb-co:hover svg{transform:none;}
    }
    `;
    var s = el("style", { id: "cb-styles" });
    s.textContent = css;
    document.head.appendChild(s);
  }

  /* ----------------------------------------------------------------------
   * 4. STATE + DOM SHELL
   * -------------------------------------------------------------------- */
  var STATE = {
    cart: null, recs: [], discountMsg: null, discountOk: false,
    lastTotal: 0, lastGap: null, lastReached: false,
    newlyAddedKeys: {},
  };
  var root, overlay, drawer, bodyEl, shipEl, resvEl, footEl, upEl, bubble;
  var resvTimer = null;

  // Lightweight number tween for "$X away" and "Subtotal" ticking up/down.
  // Uses cents internally, calls fmt(currentCents) on each frame.
  function tweenNumber(el, fromCents, toCents, fmt, duration) {
    if (!el) return;
    if (fromCents === toCents) { el.textContent = fmt(toCents); return; }
    duration = duration || 520;
    var start = performance.now();
    function step(t) {
      var p = Math.min(1, (t - start) / duration);
      // easeOutCubic — fast then settles
      var eased = 1 - Math.pow(1 - p, 3);
      var v = Math.round(fromCents + (toCents - fromCents) * eased);
      el.textContent = fmt(v);
      if (p < 1) requestAnimationFrame(step);
      else el.textContent = fmt(toCents);
    }
    requestAnimationFrame(step);
  }

  function build() {
    injectStyles();
    root = el("div", { class: "cb-root" });

    overlay = el("div", { class: "cb-overlay" });
    overlay.addEventListener("click", close);

    drawer = el("div", { class: "cb-drawer", role: "dialog", "aria-label": "Shopping cart" });

    var head = el("div", { class: "cb-head" });
    var titleWrap = el("div", { class: "cb-title-wrap" });
    if (CFG.texts.kicker) {
      titleWrap.appendChild(el("span", { class: "cb-kicker" },
        esc(CFG.texts.kicker)));
    }
    titleWrap.appendChild(el("h2", null, esc(CFG.texts.heading)));
    head.appendChild(titleWrap);
    var x = el("button", { class: "cb-x", "aria-label": "Close" },
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"' +
      ' xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor"' +
      ' stroke-width="1.8" stroke-linecap="round"/></svg>');
    x.addEventListener("click", close);
    head.appendChild(x);

    shipEl = el("div", { class: "cb-ship" });
    shipEl.style.display = "none";
    resvEl = el("div", { class: "cb-resv" });
    resvEl.style.display = "none";

    bodyEl = el("div", { class: "cb-body" });
    upEl = el("div", { class: "cb-up" });
    upEl.style.display = "none";
    footEl = el("div", { class: "cb-foot" });

    drawer.appendChild(head);
    drawer.appendChild(shipEl);
    drawer.appendChild(resvEl);
    drawer.appendChild(bodyEl);
    drawer.appendChild(upEl);
    drawer.appendChild(footEl);

    root.appendChild(overlay);
    root.appendChild(drawer);

    bubble = el("button", { class: "cb-bubble", "aria-label": "Open cart" });
    bubble.innerHTML = '<span>Cart</span><span class="cb-count">0</span>';
    bubble.addEventListener("click", open);
    bubble.style.display = "none";
    root.appendChild(bubble);

    document.body.appendChild(root);
  }

  /* ----------------------------------------------------------------------
   * 5. RENDER
   * -------------------------------------------------------------------- */
  function render() {
    var cart = STATE.cart;
    if (!cart) return;

    // bubble count
    var bc = bubble.querySelector(".cb-count");
    bc.textContent = cart.item_count;
    bubble.style.display = cart.item_count > 0 ? "flex" : "none";

    // free shipping bar
    if (CFG.freeShipping.enabled) {
      var thr = CFG.freeShipping.threshold;
      var sub = cart.total_price;
      var pct = Math.min(100, Math.round((sub / thr) * 100));
      var reached = sub >= thr;
      var gap = Math.max(0, thr - sub);
      var justUnlocked = reached && !STATE.lastReached;

      // Rebuild only when crossing threshold (message structure changes),
      // otherwise animate the gap number and bar fill in place.
      var prevGap = STATE.lastGap;
      var needsRebuild = !shipEl.querySelector(".cb-fill") ||
                         reached !== STATE.lastReached;

      if (needsRebuild) {
        var msgHtml = reached
          ? CFG.freeShipping.messageReached
          : CFG.freeShipping.messageBefore.replace(
              "{{amount}}", '<b class="cb-gap">' + money(gap) + "</b>");
        shipEl.className = "cb-ship" +
          (reached ? " cb-reached" : "") +
          (justUnlocked ? " cb-just-unlocked" : "");
        shipEl.innerHTML =
          '<p class="cb-ship-msg">' + msgHtml + "</p>" +
          '<div class="cb-track"><div class="cb-fill"></div></div>';
      } else {
        var gapEl = shipEl.querySelector(".cb-gap");
        if (gapEl && prevGap != null && prevGap !== gap) {
          tweenNumber(gapEl, prevGap, gap, money, 520);
        } else if (gapEl) {
          gapEl.textContent = money(gap);
        }
      }
      shipEl.style.display = cart.item_count > 0 ? "block" : "none";

      // Animate fill: starts from current rendered width, runs to new pct.
      requestAnimationFrame(function () {
        var f = shipEl.querySelector(".cb-fill");
        if (f) {
          // Force a layout read so the transition runs from the current pos
          // even if this is the first render.
          // eslint-disable-next-line no-unused-expressions
          f.offsetWidth;
          f.style.width = pct + "%";
        }
      });

      // One-shot celebration if we JUST crossed the threshold
      if (justUnlocked) {
        shipEl.classList.add("cb-just-unlocked");
        setTimeout(function () {
          shipEl.classList.remove("cb-just-unlocked");
        }, 1200);
        trackVante("threshold_crossed", {
          threshold: CFG.freeShipping.threshold,
          value: STATE.cart.total_price,
        });
      }

      STATE.lastGap = gap;
      STATE.lastReached = reached;
    }

    // body
    bodyEl.innerHTML = "";
    if (!cart.items || cart.items.length === 0) {
      var em = el("div", { class: "cb-empty" });
      em.appendChild(el("div", { class: "cb-empty-icon" },
        '<svg width="28" height="28" viewBox="0 0 24 24" fill="none"' +
        ' xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M5 7 H19 L17 19 H7 Z M9 7 V5 A3 3 0 0 1 15 5 V7"' +
        ' stroke="currentColor" stroke-width="1.6"' +
        ' stroke-linecap="round" stroke-linejoin="round"/></svg>'));
      em.appendChild(el("p", null, esc(CFG.texts.empty)));
      var cta = el("button", null, esc(CFG.texts.emptyCta));
      cta.addEventListener("click", close);
      em.appendChild(cta);
      bodyEl.appendChild(em);
      upEl.style.display = "none";
      footEl.style.display = "none";
      stopReservation();
      return;
    }
    footEl.style.display = "block";

    cart.items.forEach(function (it, i) {
      var line = el("div", { class: "cb-line" });
      line.style.animationDelay = i * 0.04 + "s";

      // If this exact variant was just added, give the line a brand pulse so
      // the customer sees their action confirmed.
      var addedAt = STATE.newlyAddedKeys[String(it.variant_id || it.id)];
      if (addedAt && (Date.now() - addedAt) < 1500) {
        line.classList.add("cb-just-added");
        delete STATE.newlyAddedKeys[String(it.variant_id || it.id)];
        setTimeout(function () {
          line.classList.remove("cb-just-added");
        }, 1100);
      }

      var img = el("img", {
        class: "cb-thumb",
        src: it.image || "",
        alt: esc(it.product_title || ""),
        loading: "lazy",
      });

      var info = el("div", { class: "cb-info" });
      info.appendChild(el("p", { class: "cb-title" }, esc(it.product_title)));
      if (it.variant_title && it.variant_title !== "Default Title") {
        info.appendChild(el("p", { class: "cb-variant" }, esc(it.variant_title)));
      }

      // low-stock urgency
      if (
        CFG.urgency.lowStock &&
        typeof it.inventory_quantity === "number" &&
        it.inventory_quantity > 0 &&
        it.inventory_quantity <= CFG.urgency.lowStockThreshold
      ) {
        info.appendChild(
          el("span", { class: "cb-low" }, "Only " + it.inventory_quantity + " left")
        );
      }

      var qty = el("div", { class: "cb-qty" });
      var minus = el("button", { "aria-label": "Decrease" }, "&minus;");
      var num = el("span", null, String(it.quantity));
      var plus = el("button", { "aria-label": "Increase" }, "+");
      minus.addEventListener("click", function () {
        var next = (parseInt(num.textContent, 10) || it.quantity) - 1;
        if (next < 1) { removeLine(it); return; }
        num.textContent = next;
        queueQty(it.key, next);
      });
      plus.addEventListener("click", function () {
        var next = (parseInt(num.textContent, 10) || it.quantity) + 1;
        num.textContent = next;
        queueQty(it.key, next);
      });
      qty.appendChild(minus); qty.appendChild(num); qty.appendChild(plus);
      info.appendChild(qty);

      var right = el("div", { class: "cb-right" });
      right.appendChild(el("div", { class: "cb-price" }, money(it.final_line_price)));
      var rm = el("button", { class: "cb-rm" }, esc(CFG.texts.remove));
      rm.addEventListener("click", function () { removeLine(it); });
      right.appendChild(rm);

      line.appendChild(img);
      line.appendChild(info);
      line.appendChild(right);
      bodyEl.appendChild(line);
    });

    renderUpsells();
    renderFoot();
    maybeStartReservation();
  }

  function renderUpsells() {
    if (closeTheSaleMode()) {
      var c = CFG.closeTheSale;
      upEl.style.display = "block";
      upEl.innerHTML = "";
      var box = el("div", { class: "cb-close" });
      box.appendChild(el("div", { class: "cb-close-check" },
        '<svg width="20" height="20" viewBox="0 0 24 24" fill="none">' +
        '<path d="M4 12 L10 18 L20 6" stroke="currentColor" ' +
        'stroke-width="3" stroke-linecap="round" ' +
        'stroke-linejoin="round"/></svg>'));
      if (c.message) {
        box.appendChild(el("p", { class: "cb-close-msg" }, esc(c.message)));
      }
      if (c.trustLine) {
        box.appendChild(el("p", { class: "cb-close-trust" },
          esc(c.trustLine)));
      }
      upEl.appendChild(box);
      return;
    }
    if (!CFG.upsells.enabled || !STATE.recs.length) {
      upEl.style.display = "none";
      return;
    }
    upEl.style.display = "block";
    upEl.innerHTML = "<h3>" + esc(CFG.upsells.title) + "</h3>";

    var list = STATE.recs.slice(0, CFG.upsells.maxItems);

    // Track upsell impressions — once per render. Each impression carries
    // the list of products shown so we can analyze which combos convert.
    if (list.length) {
      trackVante("upsells_viewed", {
        item_count: list.length,
        items: list.map(function (p) {
          return {
            id: p.variant_id, name: p.title, handle: p.handle || "",
            price: p.price, is_bundle: !!p._bundle,
          };
        }),
      });
    }

    // Free-shipping-aware ordering: if the shopper is short of the threshold,
    // surface the cheapest add-on that would push them over it, first.
    var gap = 0, unlockId = null;
    if (CFG.freeShipping.enabled && STATE.cart) {
      gap = CFG.freeShipping.threshold - STATE.cart.total_price;
      if (gap > 0) {
        var candidates = list
          .filter(function (p) { return p.price >= gap; })
          .sort(function (a, b) { return a.price - b.price; });
        if (candidates.length) {
          unlockId = candidates[0].variant_id;
          list = list.slice().sort(function (a, b) {
            return (b.variant_id === unlockId) - (a.variant_id === unlockId);
          });
        }
      }
    }

    list.forEach(function (p) {
      var isUnlock = p.variant_id === unlockId;
      var row = el("div", { class: "cb-up-row" + (isUnlock ? " cb-unlock" : "") });

      if (p.image) {
        row.appendChild(el("img", { src: p.image, alt: esc(p.title), loading: "lazy" }));
      } else {
        row.appendChild(el("div", { class: "cb-ph" },
          esc((p.title || "?").trim().charAt(0))));
      }

      var meta = el("div", { class: "cb-up-meta" });
      if (isUnlock) {
        meta.appendChild(el("span", { class: "cb-unlock-tag" },
          "Add this — you'll get FREE shipping"));
      }
      meta.appendChild(el("p", null, esc(p.title)));
      meta.appendChild(el("span", { class: "cb-up-price" }, money(p.price)));
      if (!isUnlock) {
        var reasonText = null;
        var isProductReason = false;
        if (p._bundle && p._bundleName && CFG.bundles &&
            CFG.bundles.reasonTemplate) {
          reasonText = CFG.bundles.reasonTemplate.replace(
            "{{bundle}}", p._bundleName);
        } else if (
          CFG.upsells.productReasons && p.handle &&
          CFG.upsells.productReasons[normHandle(p.handle)]
        ) {
          reasonText = CFG.upsells.productReasons[normHandle(p.handle)];
          isProductReason = true;
        } else if (CFG.upsells.reason) {
          reasonText = CFG.upsells.reason;
        }
        if (reasonText) {
          meta.appendChild(el("span", {
            class: "cb-up-reason" + (p._bundle ? " cb-up-bundle" : ""),
          }, esc(reasonText)));
        }
      }

      var add = el("button", { class: "cb-add" }, "Add");
      add.addEventListener("click", function () {
        add.disabled = true; add.textContent = "...";
        trackVante(p._bundle ? "bundle_added" : "upsell_added", {
          item_id: p.variant_id,
          item_name: p.title,
          item_price: p.price,
          item_handle: p.handle || "",
          bundle_name: p._bundleName || undefined,
          is_unlock: isUnlock,
          value: p.price,
        });
        addItem(p.variant_id, 1).then(function () {
          STATE.recs = STATE.recs.filter(function (x) { return x.variant_id !== p.variant_id; });
          refresh();
        }).catch(function () { add.disabled = false; add.textContent = "Add"; });
      });
      row.appendChild(meta);
      row.appendChild(add);
      upEl.appendChild(row);
    });
  }

  function renderFoot() {
    var cart = STATE.cart;
    footEl.innerHTML = "";

    if (CFG.discount.enabled) {
      if (STATE.discountCode) {
        var chip = el("div", { class: "cb-chip" });
        chip.appendChild(el("span", null,
          "✓ Code <b>" + esc(STATE.discountCode) +
          "</b> saved — applied at checkout"));
        var rmc = el("button", { "aria-label": "Remove code" }, "&times;");
        rmc.addEventListener("click", function () {
          STATE.discountCode = null;
          api("/cart/update.js", {
            method: "POST",
            body: JSON.stringify({ attributes: { discount_code: "" } }),
          });
          renderFoot();
        });
        chip.appendChild(rmc);
        footEl.appendChild(chip);
      } else {
        var dz = el("div", { class: "cb-disc" });
        var inp = el("input", {
          type: "text", placeholder: esc(CFG.discount.placeholder),
          "aria-label": "Discount code",
        });
        var db = el("button", null, "Apply");
        db.addEventListener("click", function () { applyDiscount(inp.value.trim()); });
        inp.addEventListener("keydown", function (e) {
          if (e.key === "Enter") applyDiscount(inp.value.trim());
        });
        dz.appendChild(inp); dz.appendChild(db);
        footEl.appendChild(dz);
        if (STATE.discountMsg) {
          footEl.appendChild(el("div", { class: "cb-disc-err" },
            esc(STATE.discountMsg)));
        }
      }
    }

    var sub = el("div", { class: "cb-sub" });
    sub.appendChild(el("span", null, esc(CFG.texts.subtotal)));
    var subB = el("b", null, money(STATE.lastTotal || cart.total_price));
    sub.appendChild(subB);
    footEl.appendChild(sub);
    footEl.appendChild(el("p", { class: "cb-note" }, esc(CFG.texts.taxesNote)));

    var co = el("a", { class: "cb-co", href: CFG.checkoutUrl });
    var coTotalLabel = esc(CFG.texts.checkout) + ' · ';
    co.innerHTML =
      '<span>' + coTotalLabel +
      '<span class="cb-co-amt">' + money(STATE.lastTotal || cart.total_price) +
      '</span></span>' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none"' +
      ' xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M5 12 H19 M13 6 L19 12 L13 18" stroke="currentColor"' +
      ' stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    co.addEventListener("click", function () {
      STATE.checkoutClicked = true;     // buying — never nudge this person
      trackVante("checkout_clicked", {
        value: STATE.cart.total_price,
      });
    });
    footEl.appendChild(co);

    // Tween subtotal + checkout total on change. Both elements are mounted.
    var fromTotal = STATE.lastTotal || 0;
    var toTotal = cart.total_price;
    if (fromTotal !== toTotal) {
      tweenNumber(subB, fromTotal, toTotal, money, 560);
      var coAmt = co.querySelector(".cb-co-amt");
      if (coAmt) tweenNumber(coAmt, fromTotal, toTotal, money, 560);
    } else {
      subB.textContent = money(toTotal);
    }
    STATE.lastTotal = toTotal;

    // Free-shipping ready cue on the checkout button (subtle, only when earned)
    if (CFG.freeShipping.enabled &&
        toTotal >= CFG.freeShipping.threshold) {
      co.classList.add("cb-co-ready");
    }

    if (CFG.branding && CFG.branding.showBadge) {
      var badge = el("a", {
        class: "cb-badge",
        href: CFG.branding.href || "#",
        target: "_blank", rel: "noopener",
        "aria-label": CFG.branding.label,
      });
      badge.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" ' +
        'xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M3 8 L12 19 L21 3" stroke="currentColor" stroke-width="3.4" ' +
        'stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '<span>Cart by <b>' + esc(CFG.branding.label) + "</b></span>";
      footEl.appendChild(badge);
    }
  }

  /* ----------------------------------------------------------------------
   * 6. CART OPERATIONS (Shopify AJAX API)
   * -------------------------------------------------------------------- */
  function getCart() {
    return api("/cart.js").then(function (c) {
      STATE.cart = c;
      persist(c);
      return c;
    });
  }

  function addItem(variantId, qty) {
    STATE.newlyAddedKeys[String(variantId)] = Date.now();
    return api("/cart/add.js", {
      method: "POST",
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] }),
    });
  }

  var qtyTimers = {};
  function changeLine(key, qty) {
    return api("/cart/change.js", {
      method: "POST",
      body: JSON.stringify({ id: key, quantity: Math.max(0, qty) }),
    }).then(function (c) {
      STATE.cart = c;
      persist(c);
      return c;
    });
  }

  // Debounced: rapid +/- clicks collapse into ONE request with the final
  // quantity, so the cart never races itself or lands on a wrong number.
  function queueQty(key, qty) {
    if (qtyTimers[key]) clearTimeout(qtyTimers[key]);
    qtyTimers[key] = setTimeout(function () {
      delete qtyTimers[key];
      changeLine(key, qty).then(loadRecs).then(render);
    }, 280);
  }

  function removeLine(item) {
    var snapshot = { id: item.variant_id || item.id, qty: item.quantity,
                     title: item.product_title };
    changeLine(item.key, 0).then(loadRecs).then(render).then(function () {
      toast(
        "Removed <b>" + esc(snapshot.title) + "</b>",
        {
          sticky: true,
          cta: "Undo",
          onCta: function () {
            addItem(snapshot.id, snapshot.qty).then(function () {
              refresh();
            });
          },
        }
      );
    });
  }

  function applyDiscount(code) {
    if (!code) return;
    api("/cart/update.js", {
      method: "POST",
      body: JSON.stringify({ attributes: { discount_code: code } }),
    });
    fetch("/discount/" + encodeURIComponent(code), { credentials: "same-origin" })
      .then(function (r) {
        if (r && r.ok === false) throw new Error("invalid");
        STATE.discountCode = code;
        STATE.discountOk = true;
        STATE.discountMsg = null;
        trackVante("discount_applied", { code: code });
        renderFoot();
      })
      .catch(function () {
        STATE.discountCode = null;
        STATE.discountOk = false;
        STATE.discountMsg = "That code isn't valid";
        trackVante("discount_failed", { code: code });
        renderFoot();
      });
  }

  function goTo(url) {
    // Single navigation point — overridable in tests, real in production.
    try { window.location.href = url; }
    catch (e) { window.location.assign && window.location.assign(url); }
  }

  // Used by the exit-intent recovery button. Applies a REAL Shopify code
  // and sends the shopper straight to checkout with the discount already
  // on the order. Shopify's /discount/CODE route sets the discount then
  // redirects, so it genuinely carries into checkout — not just visually.
  function applyCodeAndCheckout(code) {
    if (!code) { goTo(CFG.checkoutUrl); return; }
    api("/cart/update.js", {
      method: "POST",
      body: JSON.stringify({ attributes: { discount_code: code } }),
    });
    STATE.discountCode = code;
    try { renderFoot(); } catch (e) {}
    goTo("/discount/" + encodeURIComponent(code) +
      "?redirect=" + encodeURIComponent(CFG.checkoutUrl));
  }

  function normHandle(h) {
    return String(h || "").toLowerCase().trim();
  }

  // Returns [{handle, bundleName}] for kit members not already in the cart,
  // based on the real bundle map. Empty if bundles disabled / no match.
  function bundleTargets() {
    var b = CFG.bundles;
    if (!b || !b.enabled || !b.groups || !STATE.cart) return [];
    var inCart = {};
    STATE.cart.items.forEach(function (it) {
      if (it.handle) inCart[normHandle(it.handle)] = 1;
    });
    var seen = {}, out = [];
    b.groups.forEach(function (g) {
      var handles = (g.handles || [])
        .map(normHandle)
        .filter(function (h) { return h && h.indexOf("replace-") !== 0; });
      var anyInCart = handles.some(function (h) { return inCart[h]; });
      if (!anyInCart) return;            // cart has nothing from this kit
      handles.forEach(function (h) {
        if (inCart[h] || seen[h]) return; // already owned / already queued
        seen[h] = 1;
        out.push({ handle: h, bundleName: g.name });
      });
    });
    return out;
  }

  // True when the shopper is in "let me pay" mode: cart value over the
  // configured threshold, OR a complete kit/bundle product is in the cart.
  // In this mode all upsells are suppressed in favor of a clean close.
  function closeTheSaleMode() {
    var c = CFG.closeTheSale;
    if (!c || !c.enabled || !STATE.cart) return false;

    // (1) Free shipping crossed → stop selling, let them check out.
    // The moment the threshold is earned, the cart's job is done.
    // Showing more upsells here = pushy when they're trying to buy.
    if (CFG.freeShipping && CFG.freeShipping.enabled &&
        STATE.cart.total_price >= CFG.freeShipping.threshold) return true;

    // (2) Cart already over the close-the-sale floor (large basket).
    if (typeof c.threshold === "number" &&
        STATE.cart.total_price >= c.threshold) return true;

    // (3) A complete kit/bundle product is in the cart.
    var b = CFG.bundles;
    if (b && b.enabled) {
      var kits = (b.kitProductHandles || [])
        .map(normHandle)
        .filter(function (h) { return h && h.indexOf("replace-") !== 0; });
      if (kits.length && STATE.cart.items.some(function (it) {
        return it.handle && kits.indexOf(normHandle(it.handle)) !== -1;
      })) return true;
    }
    return false;
  }

  function fetchByHandle(handle) {
    return api("/products/" + encodeURIComponent(handle) + ".js")
      .then(function (p) {
        if (!p || !p.variants || !p.variants[0]) return null;
        var v = p.variants.filter(function (x) { return x.available; })[0]
          || p.variants[0];
        var raw = parseFloat(v.price) || 0;
        var cents = Number.isInteger(raw) ? raw : Math.round(raw * 100);
        return {
          variant_id: v.id,
          title: p.title,
          handle: p.handle || handle,
          price: cents,
          image: (p.featured_image ||
            (p.images && p.images[0]) || "") || "",
          _bundle: true,
        };
      })
      .catch(function () { return null; }); // handle typo / not a product
  }

  function loadRecs() {
    if (!CFG.upsells.enabled || !STATE.cart || !STATE.cart.items.length) {
      STATE.recs = []; return Promise.resolve();
    }
    if (closeTheSaleMode()) {        // shopper is closing — no upsells
      STATE.recs = []; return Promise.resolve();
    }
    if (CFG.upsells.strategy === "manual" && CFG.upsells.variantIds.length) {
      STATE.recs = CFG.upsells.variantIds.map(function (v) {
        return { variant_id: v, title: "Recommended add-on", price: 0, image: "" };
      });
      return Promise.resolve();
    }

    // 1) Bundle members first (real, defined relationships).
    var targets = bundleTargets();
    var bundleP = Promise.all(targets.map(function (t) {
      return fetchByHandle(t.handle).then(function (rec) {
        if (rec) rec._bundleName = t.bundleName;
        return rec;
      });
    })).then(function (list) {
      return list.filter(Boolean);
    });

    // 2) Generic recommendations fill the rest.
    var seedId = STATE.cart.items[0].product_id;
    var recP = api(
      "/recommendations/products.json?product_id=" + seedId +
      "&limit=" + (CFG.upsells.maxItems + 2) + "&intent=related"
    ).then(function (d) {
      var inCart = {};
      STATE.cart.items.forEach(function (i) { inCart[i.product_id] = 1; });
      return (d.products || [])
        .filter(function (p) { return !inCart[p.id] && p.variants && p.variants[0]; })
        .map(function (p) {
          var raw = p.variants[0].price != null
            ? p.variants[0].price : p.price;
          raw = parseFloat(raw) || 0;
          var cents = Number.isInteger(raw) ? raw : Math.round(raw * 100);
          return {
            variant_id: p.variants[0].id,
            title: p.title,
            handle: p.handle || "",
            price: cents,
            image:
              (p.featured_image ||
                (p.images && p.images[0]) ||
                (p.featured_media && p.featured_media.preview_image &&
                  p.featured_media.preview_image.src)) || "",
          };
        });
    }).catch(function () { return []; });

    return Promise.all([bundleP, recP]).then(function (res) {
      var bundles = res[0], recs = res[1];
      var have = {};
      STATE.cart.items.forEach(function (i) {
        if (i.variant_id) have[i.variant_id] = 1;
      });
      bundles.forEach(function (b) { have[b.variant_id] = 1; });
      // Bundle items first, then non-duplicate generic recs.
      STATE.recs = bundles.concat(
        recs.filter(function (r) { return !have[r.variant_id]; })
      );
    });
  }

  function refresh() {
    return getCart().then(loadRecs).then(render);
  }

  /* ----------------------------------------------------------------------
   * 7. OPEN / CLOSE + ADD-TO-CART INTERCEPTION
   * -------------------------------------------------------------------- */
  function open() {
    refresh().then(function () {
      overlay.classList.add("cb-on");
      drawer.classList.add("cb-on");
      document.documentElement.style.overflow = "hidden";
      trackVante("cart_opened", {});
    });
  }
  function close() {
    overlay.classList.remove("cb-on");
    drawer.classList.remove("cb-on");
    document.documentElement.style.overflow = "";
    maybeArmCloseNudge();
  }

  // Fires the FINISH10 offer if the shopper closes the cart (without
  // checking out) and then stays completely still for the configured
  // delay. Cancelled by any scroll/tap, or reopening the cart. Once/visit.
  var closeNudgeTimer = null;
  function maybeArmCloseNudge() {
    var rec = CFG.recovery;
    if (!rec || !rec.closeNudge) return;
    if (sessionStorage.getItem("cb_closenudge") === "1") return; // once/visit
    if (!STATE.cart || STATE.cart.item_count === 0) return;
    if (STATE.checkoutClicked) return;          // they're buying, leave alone
    if (closeNudgeTimer) clearTimeout(closeNudgeTimer);

    function cancel() {
      if (closeNudgeTimer) { clearTimeout(closeNudgeTimer); closeNudgeTimer = null; }
      window.removeEventListener("scroll", cancel, true);
      window.removeEventListener("touchstart", cancel, true);
      window.removeEventListener("pointerdown", cancel, true);
    }
    window.addEventListener("scroll", cancel, true);
    window.addEventListener("touchstart", cancel, true);
    window.addEventListener("pointerdown", cancel, true);

    closeNudgeTimer = setTimeout(function () {
      cancel();
      if (drawer.classList.contains("cb-on")) return;   // reopened
      if (!STATE.cart || STATE.cart.item_count === 0) return;
      if (sessionStorage.getItem("cb_closenudge") === "1") return;
      sessionStorage.setItem("cb_closenudge", "1");

      var hasCode = !!rec.exitIntentCode;
      var codeNote = hasCode
        ? ' Code <b>' + esc(rec.exitIntentCode) + "</b> applied automatically."
        : "";
      var ctaLabel = (hasCode && rec.exitIntentApply)
        ? "Apply 10% & checkout" : "Finish checkout";
      trackVante("nudge_shown", {
        nudge_type: "mobile_close",
        has_code: hasCode,
        code: rec.exitIntentCode || "",
      });
      toast(esc(rec.exitIntentText) + codeNote, {
        sticky: true,
        cta: ctaLabel,
        onCta: function () {
          trackVante("nudge_clicked", {
            nudge_type: "mobile_close",
            code: rec.exitIntentCode || "",
            value: STATE.cart ? STATE.cart.total_price : 0,
          });
          if (hasCode && rec.exitIntentApply) {
            applyCodeAndCheckout(rec.exitIntentCode);
          } else { open(); }
        },
      });
      if (typeof rec.onAtRisk === "function") {
        try { rec.onAtRisk(STATE.cart); } catch (e) {}
      }
    }, rec.closeNudgeDelay || 3000);
  }

  function toast(html, opts) {
    opts = opts || {};
    var t = el("div", { class: "cb-toast" });
    t.innerHTML = html;
    if (opts.sticky) {
      var x = el("button", { class: "cb-toast-x", "aria-label": "Dismiss" }, "&times;");
      x.addEventListener("click", function () {
        t.classList.remove("cb-on");
        setTimeout(function () { t.remove(); }, 400);
      });
      t.appendChild(x);
    }
    if (opts.cta) {
      var b = el("button", { class: "cb-toast-cta" }, esc(opts.cta));
      b.addEventListener("click", function () {
        if (opts.onCta) opts.onCta();
        t.classList.remove("cb-on");
        setTimeout(function () { t.remove(); }, 400);
      });
      t.appendChild(el("br"));
      t.appendChild(b);
    }
    root.appendChild(t);
    requestAnimationFrame(function () { t.classList.add("cb-on"); });
    if (!opts.sticky) {
      setTimeout(function () {
        t.classList.remove("cb-on");
        setTimeout(function () { t.remove(); }, 400);
      }, opts.duration || 3200);
    }
    return t;
  }

  function interceptForms() {
    if (!CFG.interceptAddToCart) return;
    document.addEventListener("submit", function (e) {
      var form = e.target;
      if (!form || form.getAttribute("action") == null) return;
      if (!/\/cart\/add/.test(form.getAttribute("action"))) return;
      e.preventDefault();
      var fd = new FormData(form);
      var addedId = fd.get("id");
      if (addedId) STATE.newlyAddedKeys[String(addedId)] = Date.now();
      fetch("/cart/add.js", {
        method: "POST", body: fd, credentials: "same-origin",
        headers: { "Accept": "application/json" },
      }).then(function (r) { return r.json(); }).then(function (added) {
        refresh().then(function () {
          if (CFG.openOnAdd) { open(); return; }
          var name = added && added.product_title ? added.product_title : "Item";
          toast("Added <b>" + esc(name) + "</b> to your cart", {
            duration: 5000,
            cta: CFG.texts.checkout === "Checkout" ? "View cart" : CFG.texts.heading,
            onCta: open,
          });
        });
      }).catch(function () { form.submit(); });
    }, true);
  }

  /* ----------------------------------------------------------------------
   * 8. URGENCY: soft reservation countdown
   * -------------------------------------------------------------------- */
  function maybeStartReservation() {
    if (!CFG.urgency.reservationTimer || resvTimer) return;
    var key = "cb_resv_until";
    var until = parseInt(sessionStorage.getItem(key) || "0", 10);
    var now = Date.now();
    if (!until || until < now) {
      until = now + CFG.urgency.reservationMinutes * 60000;
      sessionStorage.setItem(key, String(until));
    }
    resvEl.style.display = "flex";
    function tick() {
      var left = Math.max(0, until - Date.now());
      var m = Math.floor(left / 60000);
      var s = Math.floor((left % 60000) / 1000);
      var clock = m + ":" + (s < 10 ? "0" : "") + s;
      resvEl.innerHTML =
        "⏳ " + esc(CFG.urgency.reservationText).replace(
          "{{time}}", "<b>" + clock + "</b>"
        );
      if (left <= 0) stopReservation();
    }
    tick();
    resvTimer = setInterval(tick, 1000);
  }
  function stopReservation() {
    if (resvTimer) { clearInterval(resvTimer); resvTimer = null; }
    resvEl.style.display = "none";
  }

  /* ----------------------------------------------------------------------
   * 9. RECOVERY (client-side layer)
   * -------------------------------------------------------------------- */
  function persist(cart) {
    if (!CFG.recovery.persist) return;
    try {
      localStorage.setItem("cb_cart", JSON.stringify({
        count: cart.item_count, total: cart.total_price, at: Date.now(),
      }));
    } catch (e) {}
  }

  function recovery() {
    var rec = CFG.recovery;
    var saved;
    try { saved = JSON.parse(localStorage.getItem("cb_cart") || "null"); } catch (e) {}

    // Returning-visitor nudge
    if (rec.returningNudge && saved && saved.count > 0) {
      setTimeout(function () {
        if (STATE.cart && STATE.cart.item_count > 0 &&
            !drawer.classList.contains("cb-on")) {
          toast('You left <b>' + STATE.cart.item_count +
            ' item' + (STATE.cart.item_count > 1 ? "s" : "") +
            '</b> in your cart', {
              sticky: true, cta: CFG.texts.heading, onCta: open,
            });
        }
      }, 4000);
    }

    // Desktop exit-intent
    if (rec.exitIntent && window.matchMedia("(min-width:900px)").matches) {
      var fired = false;
      document.addEventListener("mouseout", function (e) {
        if (fired || e.clientY > 0 || !STATE.cart || STATE.cart.item_count === 0) return;
        if (drawer.classList.contains("cb-on")) return;
        fired = true;
        var hasCode = !!rec.exitIntentCode;
        var codeNote = hasCode
          ? ' Code <b>' + esc(rec.exitIntentCode) + "</b> applied automatically."
          : "";
        var ctaLabel = (hasCode && rec.exitIntentApply)
          ? "Apply 10% & checkout" : "Finish checkout";
        trackVante("nudge_shown", {
          nudge_type: "desktop_exit_intent",
          has_code: hasCode,
          code: rec.exitIntentCode || "",
        });
        toast(esc(rec.exitIntentText) + codeNote, {
          sticky: true,
          cta: ctaLabel,
          onCta: function () {
            trackVante("nudge_clicked", {
              nudge_type: "desktop_exit_intent",
              code: rec.exitIntentCode || "",
              value: STATE.cart ? STATE.cart.total_price : 0,
            });
            if (hasCode && rec.exitIntentApply) {
              applyCodeAndCheckout(rec.exitIntentCode);
            } else {
              open();
            }
          },
        });
        if (typeof rec.onAtRisk === "function") {
          try { rec.onAtRisk(STATE.cart); } catch (err) {}
        }
      });
    }

    // Generic at-risk hook for Klaviyo / PushOwl wiring
    if (typeof rec.onAtRisk === "function") {
      window.addEventListener("beforeunload", function () {
        if (STATE.cart && STATE.cart.item_count > 0) {
          try { rec.onAtRisk(STATE.cart); } catch (e) {}
        }
      });
    }
  }

  /* ----------------------------------------------------------------------
   * 10. PUBLIC API + BOOT
   * -------------------------------------------------------------------- */
  window.Vante = {
    open: open,
    close: close,
    refresh: refresh,
    add: function (variantId, qty) {
      return addItem(variantId, qty).then(function () {
        return refresh().then(function () { if (CFG.openOnAdd) open(); });
      });
    },
    getCart: function () { return STATE.cart; },
    track: trackVante,           // public — fire custom events from theme code
    config: CFG,
    version: "1.0",
    _setNav: function (fn) { goTo = fn; }, // test hook only
  };
  window.CartBooster = window.Vante; // back-compat alias

  function boot() {
    applyAutoBrand();
    build();
    interceptForms();
    refresh().then(recovery);
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && drawer.classList.contains("cb-on")) close();
    });
    // Open when user clicks any element pointing at /cart
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href$="/cart"],a[href*="/cart?"]');
      if (a && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        open();
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();